import * as React from "react"
import type { QueryClient } from "@tanstack/react-query"
import { createRootRouteWithContext, Outlet, redirect, useNavigate, useRouterState } from "@tanstack/react-router"
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools"
import { AppSidebar } from "@/components/app-shell/AppSidebar"
import { SiteHeader } from "@/components/app-shell/SiteHeader"
import { ErrorBoundary } from "@/components/error-boundary"
import { PortalAccessGate } from "@/features/authz/components/portal-access-gate"
import { getAuthenticatedTokens } from "@/lib/auth/auth-store"

function RootLayout() {
	const [sidebarOpen, setSidebarOpen] = React.useState(false)
	const location = useRouterState({ select: (state) => state.location })
	const pathname = location.pathname
	const isLoginRoute = pathname === "/"

	const handleCloseSidebar = React.useCallback(() => {
		setSidebarOpen(false)
	}, [])

	const handleOpenSidebar = React.useCallback(() => {
		setSidebarOpen(true)
	}, [])

	if (isLoginRoute) {
		return (
			<div className="min-h-screen bg-background text-foreground">
				<Outlet />
			</div>
		)
	}

	return (
		<ErrorBoundary title="Authentication error" fallback={<AuthRedirectFallback />}>
			<PortalAccessGate>
				<div className="min-h-screen flex bg-background text-foreground">
					{sidebarOpen ? (
						<div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={handleCloseSidebar} />
					) : null}

					<AppSidebar isOpen={sidebarOpen} onClose={handleCloseSidebar} />

					<div className="flex min-h-screen min-w-0 w-full flex-1 flex-col lg:w-auto">
						<SiteHeader onOpenSidebar={handleOpenSidebar} />
						<main className="mx-auto w-full max-w-[1600px] flex-1 overflow-x-hidden p-6 lg:p-8">
							<Outlet />
						</main>
						<TanStackRouterDevtools position="bottom-right" />
					</div>
				</div>
			</PortalAccessGate>
		</ErrorBoundary>
	)
}

function AuthRedirectFallback() {
	const navigate = useNavigate()

	React.useEffect(() => {
		void navigate({ to: "/" })
	}, [navigate])

	return (
		<div
			className="min-h-screen flex items-center justify-center text-body text-muted-foreground"
			role="status"
			aria-live="polite"
		>
			Redirecting to sign in…
		</div>
	)
}

type PortalRouterContext = {
	readonly queryClient: QueryClient
}

export const Route = createRootRouteWithContext<PortalRouterContext>()({
	component: RootLayout,
	beforeLoad: ({ location }) => {
		const pathname = location.pathname
		const tokens = getAuthenticatedTokens()

		if (!tokens) {
			if (pathname !== "/") {
				throw redirect({ to: "/" })
			}
			return
		}

		if (pathname === "/") {
			throw redirect({ to: "/dashboard" })
		}
	},
})
